import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BookOpen, Wrench } from "lucide-react";
import { buildMarkdownComponents } from "@/lib/helpMarkdown";
import { exportNodeToPdf } from "@/lib/pdfExport";
import { useHelpDocs } from "@/hooks/useHelpDocs";
import { useDocSearch } from "@/hooks/useDocSearch";
import { HelpHeadingHits, HelpSearchBar } from "@/components/help/HelpSearch";
import { HelpDocBody, HelpPdfButton } from "@/components/help/HelpDoc";

const DOCS = [
  {
    key: "manual", label: "Manual del Administrador", icon: BookOpen, testid: "help-tab-manual",
    description: "Guía funcional para operar la plataforma: usuarios, plantillas, publicación, auditoría y despliegue gradual.",
  },
  {
    key: "technical", label: "Documentación Técnica", icon: Wrench, testid: "help-tab-technical",
    description: "Arquitectura, modelo de datos, endpoints REST, flujo SSO Entra ID y roadmap de deuda técnica.",
  },
];
const DOC_KEYS = DOCS.map((d) => d.key);

export default function Help() {
  const [active, setActive] = useState(DOCS[0].key);
  const [downloading, setDownloading] = useState(null);
  const { docs, loading } = useHelpDocs(DOC_KEYS);
  // Mounted doc nodes live in state so search re-indexes when a tab mounts.
  const [nodes, setNodes] = useState({});
  const nodeRefs = useMemo(() => Object.fromEntries(DOC_KEYS.map((key) => [
    key, (el) => setNodes((prev) => (prev[key] === el ? prev : { ...prev, [key]: el })),
  ])), []);
  const search = useDocSearch(nodes[active], docs[active]?.content, `${active}:${loading}`);
  const components = useMemo(() => buildMarkdownComponents(search.highlighter), [search.highlighter]);
  const activeMeta = DOCS.find((d) => d.key === active);

  const downloadPdf = async () => {
    const node = nodes[active];
    if (!node) return;
    setDownloading(active);
    try {
      await exportNodeToPdf(node, (docs[active]?.filename || `${active}.md`).replace(/\.md$/i, ".pdf"));
      toast.success("PDF generado correctamente");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("PDF generation failed", err);
      toast.error("No se pudo generar el PDF");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="help-root">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-600 mb-1">Centro de ayuda</div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Ayuda y documentación</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">{activeMeta?.description}</p>
        </div>
        <HelpPdfButton onClick={downloadPdf} downloading={downloading === active}
          disabled={loading || downloading === active || !docs[active]} />
      </div>

      <HelpSearchBar search={search} />
      {search.query && <HelpHeadingHits headings={search.matchingHeadings} onJump={search.scrollToHeading} />}

      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="bg-slate-100">
          {DOCS.map((d) => (
            <TabsTrigger key={d.key} value={d.key} data-testid={d.testid}>
              <d.icon className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />
              {d.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {DOCS.map((d) => (
          <TabsContent key={d.key} value={d.key} className="mt-4">
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
              <div ref={nodeRefs[d.key]} className="px-8 py-8 max-w-none"
                data-testid={`help-content-${d.key}`}>
                <HelpDocBody loading={loading} content={docs[d.key]?.content} components={components} />
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>

    </div>
  );
}
